import React, { useContext, useMemo, useState } from 'react'
import type { TieredPackageTierId } from 'paintscout'
import TieredPackageGate from './TieredPackageGate'

export interface TieredPackageGateContextValue {
  /** True until the customer picks a package. While it is, the estimate shows the gate in its place. */
  showGate: boolean
  /** Called with the picked tier. The provider's owner sets `showGate` to false. */
  onViewTier: (tierId: TieredPackageTierId) => void
}

const TieredPackageGateContext = React.createContext<TieredPackageGateContextValue>({
  showGate: false,
  onViewTier: () => {}
})

/**
 * Supplied by the surfaces that show the customer the gate: the customer view and the estimator's
 * preview of it. Every other surface has no provider, so its estimate renders as before.
 */
export const TieredPackageGateProvider = TieredPackageGateContext.Provider

export function useTieredPackageGate() {
  return useContext(TieredPackageGateContext)
}

/**
 * The provider value for a surface that shows the gate: it starts at `initialShowGate` and hides the
 * gate once a tier is picked.
 */
export function useTieredPackageGateState(initialShowGate: boolean): TieredPackageGateContextValue {
  const [showGate, setShowGate] = useState(initialShowGate)
  return useMemo(() => ({ showGate, onViewTier: () => setShowGate(false) }), [showGate])
}

/**
 * The estimate's slot in a presentation (ENG-2568). Until the customer picks a package, the package
 * cards fill that slot; the rest of the presentation stays visible around them. Picking a tier puts
 * the estimate back in the same place.
 */
export function PackageGateOrEstimate({ children }: { children: React.ReactNode }) {
  const { showGate, onViewTier } = useTieredPackageGate()

  if (showGate) {
    return <TieredPackageGate onViewTier={onViewTier} />
  }

  return <>{children}</>
}
